/** My profile (mockup v7): profile, companies & roles, notifications, sign-in, activity. The old Administration tab is now the Settings menu (2026-10-23). */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, type MyActivity } from "../lib/api";
import { useStore } from "../lib/store";
import { useAuth } from "../auth/AuthProvider";
import { LOCAL_AUTH, changePassword, cognitoMessage } from "../auth/cognito";
import { MS_SIGNIN_KEY } from "../auth/microsoft";
import { coName } from "../lib/brand";
import { cap, fmt, fmtDT } from "../lib/format";
import { MfaSetupPanel } from "../auth/MfaSetup";
import { Avatar, Co, Empty, Loading, Tile, TypeBadge } from "../components/bits";

type Tab = "profile" | "access" | "notifications" | "security" | "activity";
const VERB: Record<string, string> = {
  "document.create": "Started a draft of", "version.create": "Uploaded a new file for", "version.submit": "Submitted",
  "version.withdraw": "Withdrew", "version.approve": "Approved", "version.changes_requested": "Requested changes on",
};
const NOTIFY_DEFAULTS = { approvals: true, changes: true, approved: true, review: true, reviewDays: 30, published: false, digest: true, acks: true, agreements: true, suggestions: true, expiry: true, task_reminders: true, daily_summary: false, done_notices: true };

export function ProfilePage() {
  const { me, p, approvalsCount, draftsCount } = useStore();
  const [sp, setSp] = useSearchParams();
  const [act, setAct] = useState<MyActivity | null>(null);
  useEffect(() => { api.myActivity().then(setAct, () => setAct({ stats: { owned_published: 0, approved: 0, drafts: 0 }, items: [] })); }, []);

  const tabs: [Tab, string][] = [["profile", "Profile"], ["access", "Companies & roles"], ["notifications", "Notifications"], ["security", "Sign-in & security"], ["activity", "My activity"]];
  const tab = (tabs.find((t) => t[0] === sp.get("tab"))?.[0] ?? "profile") as Tab;
  const u = me.user;

  return (
    <div className="view">
      <div className="card prof-head">
        <div className="bigav"><Avatar id={u.id} name={u.display_name} size={72} /></div>
        <div className="who2">
          <h2>{u.display_name}</h2>
          <div className="sub2">{u.title ? `${u.title} · ` : ""}{u.email}</div>
          <div className="rolechips">
            {me.companies.map((c) => (
              <span key={c.code} className="co-roles">
                <Tile code={c.code} />
                <b>{coName(c.code, c.name)}</b>
                {p.rolesIn(c.code).filter((r) => r !== "reader" || p.rolesIn(c.code).length === 1).map((r) => <span key={r} className={`rp rp-${r}`}>{cap(r)}</span>)}
              </span>
            ))}
            {!me.companies.length && <span className="help">No company access yet. An administrator adds you in Settings &gt; Team.</span>}
          </div>
        </div>
      </div>
      <div className="kpis prof-kpis">
        <Link className="kc" to="/library" style={{ ["--kc" as string]: "#2F6FB3" }}><span className="v">{act?.stats.owned_published ?? "–"}</span><span className="l">Published documents you own</span></Link>
        <Link className="kc" to="/profile?tab=activity" style={{ ["--kc" as string]: "#2F7A52" }}><span className="v">{act?.stats.approved ?? "–"}</span><span className="l">Revisions you've approved</span></Link>
        <Link className="kc" to="/approvals" style={{ ["--kc" as string]: "#E0A31A" }}><span className="v">{approvalsCount}</span><span className="l">Waiting on your approval</span></Link>
        <Link className="kc" to="/drafts" style={{ ["--kc" as string]: "#7C5CC4" }}><span className="v">{draftsCount}</span><span className="l">Your drafts and submissions</span></Link>
      </div>
      <div className="tabs" role="tablist">
        {tabs.map(([k, l]) => <button key={k} className={`tab ${tab === k ? "on" : ""}`} role="tab" aria-selected={tab === k} onClick={() => setSp(k === "profile" ? {} : { tab: k }, { replace: true })}>{l}</button>)}
      </div>
      {tab === "profile" && <ProfileTab act={act} approvalsCount={approvalsCount} draftsCount={draftsCount} />}
      {tab === "access" && <AccessTab />}
      {tab === "notifications" && <NotificationsTab />}
      {tab === "security" && <SecurityTab />}
      {tab === "activity" && <ActivityTab act={act} />}
    </div>
  );
}

function ProfileTab({ act, approvalsCount, draftsCount }: { act: MyActivity | null; approvalsCount: number; draftsCount: number }) {
  const { me, p, toast } = useStore();
  const { refresh } = useAuth();
  const u = me.user;
  const [name, setName] = useState(u.display_name);
  const [title, setTitle] = useState(u.title ?? "");
  const [phone, setPhone] = useState(u.phone ?? "");
  const [wsDef, setWsDef] = useState(me.prefs.default_tenant || "ALL");
  const [tz, setTz] = useState(me.prefs.time_zone || "America/New_York");
  const [busy, setBusy] = useState(false);
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return toast("Display name can't be empty.");
    setBusy(true);
    try { await api.updateMe({ display_name: name.trim(), title: title.trim(), phone: phone.trim(), default_tenant: wsDef, time_zone: tz }); toast("Profile saved."); await refresh(); }
    catch (err) { toast((err as Error).message); }
    finally { setBusy(false); }
  };
  const wsOptions = (p.myCodes.length > 1 ? [["ALL", "All my companies"]] : []).concat(me.companies.map((c) => [c.code, coName(c.code, c.name)]));
  return (
    <div className="prof-grid">
      <div className="card sect">
        <h3>Personal details</h3>
        <p className="cap">How your name appears on approvals, revision history and the viewer stamp.</p>
        <form className="fgrid" onSubmit={save}>
          <div className="fg-head">Who you are</div>
          <label><span className="lbl">Display name</span><input className="inp" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} /></label>
          <label><span className="lbl">Job title</span><input className="inp" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} /></label>
          <label><span className="lbl">Work email</span><input className="inp" value={u.email} disabled title="Managed by your administrator" /><span className="help">Managed by your administrator.</span></label>
          <label><span className="lbl">Phone</span><input className="inp" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Optional" maxLength={120} /></label>
          <div className="fg-head">Preferences</div>
          <label><span className="lbl">Default workspace</span>
            <select className="inp" value={wsDef} onChange={(e) => setWsDef(e.target.value)}>{wsOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            <span className="help">Where you land after signing in.</span></label>
          <label><span className="lbl">Time zone</span>
            <select className="inp" value={tz} onChange={(e) => setTz(e.target.value)}>{["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles"].map((z) => <option key={z}>{z}</option>)}</select></label>
          <div style={{ gridColumn: "1/-1", display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" className="btn btn-ghost" onClick={() => { setName(u.display_name); setTitle(u.title ?? ""); setPhone(u.phone ?? ""); }}>Cancel</button>
            <button className={`btn btn-accent ${busy ? "busy" : ""}`} type="submit">Save changes</button>
          </div>
        </form>
      </div>
      <div className="card sect">
        <h3>Recent activity</h3>
        <p className="cap">Your latest approvals and submissions.</p>
        {!act ? <Loading /> : act.items.length ? (
          <div className="mini-act">
            {act.items.slice(0, 6).map((a, i) => {
              const self = a.action === "version.approve" && a.details?.self_approved === true;
              return (
                <Link key={i} className="ma-row" to={`/doc/${a.document_id}`}>
                  <TypeBadge type={a.type_code} sm />
                  <span className="ma-tx"><b>{self ? "Self-approved" : VERB[a.action] ?? a.action}</b> {a.title}<small>{a.base_number} · {fmtDT(a.at)}</small></span>
                </Link>
              );
            })}
            <Link className="linkbtn tiny" to="/profile?tab=activity">All my activity →</Link>
          </div>
        ) : <p className="cap">Nothing yet. Your approvals and submissions will show here.</p>}
      </div>
    </div>
  );
}

function AccessTab() {
  const { me, p } = useStore();
  const yes = <span className="ok-badge">✓ Yes</span>, no = <span style={{ color: "var(--faint)" }}>-</span>;
  return (
    <div className="card">
      <div className="sect" style={{ paddingBottom: 0 }}><h3>Companies &amp; roles</h3><p className="cap">What you can do in each company. {p.anyAdmin ? "Change roles in Settings > People & roles." : "Ask an administrator if something needs to change."}</p></div>
      <table className="tbl dt access">
        <colgroup><col /><col style={{ width: 96 }} /><col style={{ width: 96 }} /><col style={{ width: 96 }} /><col style={{ width: 96 }} /><col style={{ width: 96 }} /></colgroup>
        <thead><tr><th>Company</th><th>Reader</th><th>Author</th><th>Reviewer</th><th>Approver</th><th>Admin</th></tr></thead>
        <tbody>
          {me.companies.map((c) => (
            <tr key={c.code} style={{ cursor: "default" }}>
              <td><span style={{ display: "inline-flex", gap: 10, alignItems: "center" }}><Tile code={c.code} /><b style={{ color: "var(--ink)", fontWeight: 600 }}>{coName(c.code, c.name)}</b></span></td>
              {(["reader", "author", "reviewer", "approver", "admin"] as const).map((r) => <td key={r}>{p.has(c.code, r) ? yes : no}</td>)}
            </tr>
          ))}
          {me.group && (
            <tr style={{ cursor: "default" }}>
              <td><span style={{ display: "inline-flex", gap: 10, alignItems: "center" }}><span className="tile txt" style={{ ["--tc" as string]: "#3E4651" }}>CPY</span><b style={{ color: "var(--ink)", fontWeight: 600 }}>Company-wide documents</b></span></td>
              {(["reader", "author", "reviewer", "approver", "admin"] as const).map((r) => <td key={r}>{p.has("CPY", r) ? yes : no}</td>)}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function NotificationsTab() {
  const { me, p, toast } = useStore();
  const { refresh } = useAuth();
  const [n, setN] = useState({ ...NOTIFY_DEFAULTS, ...(me.prefs.notify ?? {}) });
  const save = async (patch: Partial<typeof n>, msg: string) => {
    const next = { ...n, ...patch };
    setN(next);
    try { await api.updateMe({ notify: patch }); toast(msg); void refresh(); }
    catch (e) { toast((e as Error).message); setN(n); }
  };
  const row = (k: keyof typeof NOTIFY_DEFAULTS, label: string, help: string) => (
    <div className="setrow" key={k}><div className="tx"><b>{label}</b><span>{help}</span></div>
      <label className="switch"><input type="checkbox" checked={!!n[k]} onChange={(e) => void save({ [k]: e.target.checked }, "Notification preference saved.")} aria-label={label} /><span /></label></div>
  );
  return (
    <div className="prof-grid">
      <div className="card sect">
        <h3>Email notifications</h3>
        <p className="cap">Sent to {me.user.email} from docvault@itassurancemsp.com.</p>
        {p.anyApprover && row("approvals", "Approval requests", "When a document is submitted to you or to any approver in your companies")}
        {p.anyAuthor && row("changes", "Changes requested", "When an approver sends one of your documents back")}
        {p.anyAuthor && row("approved", "My document was approved", "When your revision is approved and published")}
        {row("review", "Review reminders", "When a document you own is due for review")}
        <div className="setrow"><div className="tx"><b>Remind me</b><span>How far ahead of the review date</span></div>
          <select className="inp" style={{ width: 140 }} value={n.reviewDays} onChange={(e) => void save({ reviewDays: Number(e.target.value) }, `Review reminders ${e.target.value} days before.`)}>
            {[14, 30, 60, 90].map((d) => <option key={d} value={d}>{d} days before</option>)}
          </select></div>
        {row("published", "New documents published", "Every time a document is published in your companies")}
        {row("acks", "Documents to acknowledge", "When a document you must read and acknowledge is published or changes")}
        {p.chCodes.length > 0 && row("agreements", "Agreement deadlines (TechHub)", "90, 60, 30 and 7 days before an agreement's notice date or end, for clients you own (every client, for admins and Accounting)")}
        {p.chCodes.length > 0 && row("expiry", "SSL and domain expiry (TechHub)", "Before a client's website certificate or domain registration runs out")}
        {row("suggestions", "Suggested changes", "When someone suggests a change to a document you own, and when your suggestion is answered")}
        {row("digest", "Weekly digest", "Monday summary email: new and updated documents, reviews coming due, documents to acknowledge")}
        <h3 style={{ marginTop: 18 }}>My tasks</h3>
        {row("task_reminders", "Reminder if it's still waiting", "A reminder 3 days after something lands in My tasks, then once a week until it's done")}
        {row("done_notices", "Done notices", "When something you were waiting on is done: a reminder you set, a step on your client's checklist, a finished checklist")}
        {row("daily_summary", "Daily summary", "One email each morning listing everything in My tasks, instead of separate reminders. Skipped on days with nothing new.")}
      </div>
      <div className="card sect"><h3>Slack and Microsoft Teams</h3><p className="cap">The same notices as a direct message, once an admin connects your company's Slack or Teams.</p><div className="note info" style={{ display: "block" }}>Planned for a later release.</div></div>
    </div>
  );
}

function SecurityTab() {
  const { toast } = useStore();
  const [open, setOpen] = useState(false);
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [busy, setBusy] = useState(false);
  const usedMicrosoft = !LOCAL_AUTH && localStorage.getItem(MS_SIGNIN_KEY) === "microsoft"; // this session came through Microsoft
  const change = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPw.length < 12) return toast("Use at least 12 characters.");
    setBusy(true);
    try { await changePassword(oldPw, newPw); toast("Password changed."); setOpen(false); setOldPw(""); setNewPw(""); }
    catch (err) { toast(cognitoMessage(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="prof-grid">
      <div className="card sect">
        <h3>Sign-in</h3>
        <p className="cap">How you get into the document library.</p>
        <div className="setrow"><div className="tx"><b>Password</b><span>{LOCAL_AUTH ? "Not used in local development" : "Change it any time; at least 12 characters"}</span></div>
          {!LOCAL_AUTH && <button className="btn btn-ghost btn-sm" onClick={() => setOpen((o) => !o)}>Change password</button>}</div>
        {open && (
          <form onSubmit={change} className="fgrid" style={{ padding: "4px 0 12px" }}>
            <label><span className="lbl">Current password</span><input className="inp" type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} autoComplete="current-password" /></label>
            <label><span className="lbl">New password</span><input className="inp" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} autoComplete="new-password" /></label>
            <div style={{ gridColumn: "1/-1", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>Cancel</button>
              <button className={`btn btn-accent btn-sm ${busy ? "busy" : ""}`} type="submit">Save password</button>
            </div>
          </form>
        )}
        <TwoStepRow />
        <div className="setrow"><div className="tx"><b>Sign in with Microsoft</b><span>On the sign-in page, type your work email and choose Sign in with Microsoft. Your Microsoft 365 multi-factor sign-in then protects DocVault, and DocVault doesn't ask for its own code.</span></div>{usedMicrosoft ? <span className="pill sm pub">In use</span> : <span className="pill sm draft">Not used yet</span>}</div>
      </div>
      <SignOutEverywhere />
    </div>
  );
}

/** Two-step sign-in (approved mockup v10, 2026-10-27): status, and moving it to a new phone. */
function TwoStepRow() {
  const { me, refresh } = useAuth();
  const { toast } = useStore();
  const [moving, setMoving] = useState(false);
  const m = me?.mfa;
  if (!m) return null;
  if (m.method === "microsoft") return (
    <div className="setrow"><div className="tx"><b>Two-step sign-in</b><span>You signed in with Microsoft this time, so your company's Microsoft sign-in did the second step. If you sign in with a password, DocVault asks for a code from your authenticator app.</span></div>
      {m.on ? <span className="pill sm pub">On</span> : <span className="pill sm draft">Not set up</span>}</div>
  );
  return (<>
    <div className="setrow"><div className="tx"><b>Two-step sign-in {m.on ? <span className="pill sm pub">On</span> : <span className="pill sm draft">Not set up</span>}</b>
      <span>A code from your authenticator app each time you sign in with a password.{m.since ? ` Set up ${fmt(m.since)}.` : ""}</span></div>
      {!moving && <button className="btn btn-ghost btn-sm" onClick={() => setMoving(true)}>{m.on ? "Move to a new phone" : "Set up"}</button>}</div>
    {moving && <div style={{ padding: "4px 0 14px" }}>
      <MfaSetupPanel email={me.user.email} onCancel={() => setMoving(false)} onDone={() => { setMoving(false); toast("Two-step sign-in now uses your new phone. The old one no longer works."); void refresh(); }} />
    </div>}
  </>);
}

/** Sign out of every browser and device (2026-10-22): lost laptop, shared PC. */
function SignOutEverywhere() {
  const { signOut } = useAuth();
  const { toast } = useStore();
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!window.confirm("Sign out of DocVault on every browser and device, including this one?")) return;
    setBusy(true);
    try { await api.signOutEverywhere(); signOut(); }
    catch (e) { toast((e as Error).message); setBusy(false); }
  };
  return (
    <div className="card sect"><h3>Signing out</h3>
      <p className="cap">Sign out from the bottom of the menu on the left. Your session also ends after a period of inactivity.</p>
      <div className="setrow"><div className="tx"><b>Sign out everywhere</b><span>Lost a laptop or used a shared computer? This ends your DocVault sessions on every browser and device. Any other open window stops working within the hour.</span></div>
        <button className={`btn btn-ghost btn-sm ${busy ? "busy" : ""}`} onClick={() => void go()}>Sign out everywhere</button></div>
    </div>
  );
}

function ActivityTab({ act }: { act: MyActivity | null }) {
  const nav = useNavigate();
  if (!act) return <Loading />;
  return (
    <div className="card">
      <div className="sect" style={{ paddingBottom: 4 }}><h3>My activity</h3><p className="cap">Your approvals, submissions and change requests. The full audit log is kept for every action.</p></div>
      {act.items.length ? (
        <table className="tbl dt">
          <colgroup><col style={{ width: 130 }} /><col /><col className="hide-md" style={{ width: 200 }} /></colgroup>
          <tbody>
            {act.items.slice(0, 20).map((a, i) => {
              const self = a.action === "version.approve" && a.details?.self_approved === true;
              const rev = a.action === "version.approve" ? (typeof a.details?.version === "string" ? ` v${a.details.version}` : typeof a.details?.revision === "number" ? ` v${a.details.revision}.0.0` : "") : "";
              return (
                <tr key={i} onClick={() => nav(`/doc/${a.document_id}`)}>
                  <td className="due">{fmtDT(a.at)}</td>
                  <td><span style={{ display: "inline-flex", gap: 10, alignItems: "center", minWidth: 0 }}><TypeBadge type={a.type_code} sm />
                    <span style={{ minWidth: 0 }}><b style={{ color: "var(--ink)", fontWeight: 600 }}>{self ? "Self-approved" : VERB[a.action] ?? a.action} {a.base_number}{rev}</b><br /><span style={{ fontSize: 12, color: "var(--muted)" }}>{a.title}</span></span></span></td>
                  <td className="hide-md"><Co code={a.company_code} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : <Empty title="Nothing yet">Your approvals and submissions will show here.</Empty>}
    </div>
  );
}
